import dayFactory, {
  WEEKDAY_PARAMS,
  WeekdayQueryParameter,
} from '@rover/react-lib/src/factories/dayFactory';
import BusinessType from '@rover/shared/js/constants/businessType.constants';
import { DEFAULT_LANG } from '@rover/shared/js/constants/i18n/language';
import {
  ARRAY_FIELDS,
  ARRAY_NUMBER_FIELDS,
  BOOLEAN_FIELDS,
  DATE_FIELDS,
  NUMBER_FIELDS,
  SEARCH_QUERY_FALLBACK_FIELDS,
  SEARCH_QUERY_OVERRIDE_FIELDS,
} from '@rover/shared/js/constants/searchPage.constants';
import type { Day, SearchFilters } from '@rover/types';

import parseDate from './parseDate';
import { searchApiKeysMap } from '.';

export type QueryFilterParams = Record<string, string | boolean | undefined>;
export type QueryFilterArrayParams = Record<string, string[] | undefined>;
type WeekdayFilterParams = {
  selectedDays: Day[];
};

export const getFilterParams = (query: QueryFilterParams): QueryFilterParams => {
  const filterParams = {};

  Object.keys(query)
    .filter((key) => searchApiKeysMap[key])
    .forEach((key) => {
      filterParams[searchApiKeysMap[key]] = query[key];
    });

  return filterParams;
};

export const parseNumberFilters = (filters: QueryFilterParams): Record<string, number> => {
  const parsedFilters = {};

  NUMBER_FIELDS.forEach((field) => {
    const filter = filters[field];
    if (typeof filter === 'string') {
      parsedFilters[field] = parseInt(filter, 10);
    }
  });

  return parsedFilters;
};

export const parseBooleanFilters = (filters: QueryFilterParams): Record<string, boolean> => {
  const parsedFilters = {};

  BOOLEAN_FIELDS.forEach((field) => {
    const fieldName = typeof field === 'string' ? field : field.paramName;
    if (fieldName in filters) {
      parsedFilters[fieldName] = String(filters[fieldName]).toLowerCase() === 'true';
    }
  });

  return parsedFilters;
};

export const parseArrayFilters = (
  filters: QueryFilterParams | QueryFilterArrayParams
): Record<string, string | number> => {
  const parsedFilters = {};

  const decodeArrayFieldValue = (field: string) => (v: string) =>
    ARRAY_NUMBER_FIELDS.has(field) ? parseInt(decodeURIComponent(v), 10) : decodeURIComponent(v);

  ARRAY_FIELDS.forEach((field) => {
    if (field in filters) {
      const filter = filters[field];
      if (filter && typeof filter !== 'boolean') {
        if (Array.isArray(filter)) {
          parsedFilters[field] = filter.map(decodeArrayFieldValue(field));
        } else {
          parsedFilters[field] = filter.split(',').map(decodeArrayFieldValue(field));
        }
      } else {
        parsedFilters[field] = [];
      }
    }
  });

  return parsedFilters;
};

export const parseDateFilters = (
  filters: QueryFilterParams,
  language: string = DEFAULT_LANG
): Record<string, string | Date | undefined> => {
  const parsedFilters = {};

  DATE_FIELDS.forEach((field) => {
    if (field in filters) {
      const filter = filters[field];
      parsedFilters[field] =
        typeof filter === 'string' ? parseDate(decodeURIComponent(filter), language) : '';
    }
  });

  return parsedFilters;
};

export const parseWeekdayFilters = (filters: QueryFilterParams): WeekdayFilterParams => {
  const parsedFilters: WeekdayFilterParams = { selectedDays: [] };

  parsedFilters.selectedDays = WEEKDAY_PARAMS.reduce(
    (selected: Day[], weekday: WeekdayQueryParameter) => {
      if (filters[weekday.paramName] === 'true') {
        selected.push(dayFactory({ name: weekday.paramName, id: weekday.index }));
      }
      return selected;
    },
    []
  );

  return parsedFilters;
};

export const mergeFallbackValuesFromServerQuery = (
  urlQueryParams: QueryFilterParams,
  serverQuery: Partial<SearchFilters>
): QueryFilterParams => {
  const fallbackValues = {};
  // For some filters, default values cannot be reliably hard coded
  // in factories, e.g. pet, since we don't know what those default values
  // are until after performing a search.
  if (serverQuery) {
    SEARCH_QUERY_FALLBACK_FIELDS.forEach((param) => {
      if (urlQueryParams[param] === undefined && serverQuery[param]) {
        fallbackValues[param] = serverQuery[param];
      }
    });
  }
  return { ...urlQueryParams, ...fallbackValues };
};

export const mergeOverrideValuesFromServerQuery = (
  urlQueryParams: QueryFilterParams,
  serverQuery: Partial<SearchFilters>
): QueryFilterParams => {
  const overrideValues = {};
  // Discard invalid dates by using the validated dates from the server
  if (serverQuery) {
    SEARCH_QUERY_OVERRIDE_FIELDS.forEach((param) => {
      if (urlQueryParams[param] && serverQuery[param]) {
        overrideValues[param] = serverQuery[param];
      }
    });
  }
  return { ...urlQueryParams, ...overrideValues };
};

export const parseDaycareTypeFilters = (
  urlQueryParameters: QueryFilterParams
): Partial<SearchFilters> => {
  const daycareFilters: Partial<SearchFilters> = {};

  if (!urlQueryParameters.daycareType) return daycareFilters;
  const daycareTypeString = urlQueryParameters.daycareType as string;

  daycareFilters.inSittersHome = daycareTypeString.includes('sitter');
  daycareFilters.atDaycareFacility = daycareTypeString.includes('facility');

  return daycareFilters;
};
export const parseBusinessTypeFilters = (
  urlQueryParameters: QueryFilterParams
): Partial<SearchFilters> => {
  const businessFilters: Partial<SearchFilters> = {};
  if (!urlQueryParameters.businessType) return businessFilters;
  const businessTypeString = urlQueryParameters.businessType as BusinessType;
  return {
    businessType: businessTypeString.includes(',')
      ? (businessTypeString.split(',') as BusinessType[])
      : businessTypeString,
  };
};

/**
 * query:  the current url params as a JS object
 * serverQuery:  the search query used by the search API, which overrides some filters
 */
export default (
  query: QueryFilterParams,
  serverQuery: Partial<SearchFilters>,
  language?: string
): Partial<SearchFilters> => {
  let filterParams = getFilterParams(query);
  filterParams = mergeFallbackValuesFromServerQuery(filterParams, serverQuery);
  filterParams = mergeOverrideValuesFromServerQuery(filterParams, serverQuery);

  return {
    ...filterParams,
    ...parseNumberFilters(filterParams),
    ...parseBooleanFilters(filterParams),
    ...parseArrayFilters(filterParams),
    ...parseDateFilters(filterParams, language),
    ...parseWeekdayFilters(filterParams),
    ...parseDaycareTypeFilters(filterParams),
    ...parseBusinessTypeFilters(filterParams),
  };
};
